import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  ShoppingCart,
  User,
  Building2,
  Package,
  Plus,
  Trash2,
  Calendar,
  MapPin
} from 'lucide-react';
import { toast } from 'react-toastify';
import { formatDateToISO } from '../../utils/formatters';
import { createOrder, updateOrder } from '../../api/wholesale/orders';
import { getDistributors } from '../../api/wholesale/distributors';
import { getCompanies } from '../../api/wholesale/companies';
import { getCompanyProducts } from '../../api/wholesale/companyProducts';
import { getSizeCategories } from '../../api/wholesale/sizeCategories';
import { getCodePrices, getPriceZones } from '../../api/wholesale/companyPricing';
import { getCompanySizeWeights } from '../../api/wholesale/companySizeWeights';

const CreateOrderModal = ({ isOpen, onClose, onSuccess, editOrder = null }) => {
  const navigate = useNavigate();
  const isEditMode = !!editOrder;

  const [formData, setFormData] = useState({
    distributor_id: '',
    manufacturing_company_id: '',
    requested_delivery_date: '',
    delivery_address: '',
    notes: '',
    items: [{ size_category_id: '', code_pattern: '', quantity_cartons: '', unit_price: '' }]
  });

  const [distributors, setDistributors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyProducts, setCompanyProducts] = useState([]);
  const [sizeCategories, setSizeCategories] = useState([]);
  const [codePrices, setCodePrices] = useState([]);
  const [priceZones, setPriceZones] = useState([]);
  const [sizeWeights, setSizeWeights] = useState([]);
  const [customerZone, setCustomerZone] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingCompanyData, setLoadingCompanyData] = useState(false);
  const [initialItemsSet, setInitialItemsSet] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchData();
      setInitialItemsSet(false);

      // If editing an order, pre-populate form data (except items - those need products loaded first)
      if (editOrder) {
        setFormData((prev) => ({
          ...prev,
          distributor_id: editOrder.distributor_id || '',
          manufacturing_company_id: editOrder.manufacturing_company_id || '',
          requested_delivery_date: editOrder.requested_delivery_date
            ? formatDateToISO(new Date(editOrder.requested_delivery_date))
            : '',
          delivery_address: editOrder.delivery_address || '',
          notes: editOrder.notes || '',
          // Initialize with existing items - products will render once company data loads
          items:
            editOrder.items?.length > 0
              ? editOrder.items.map((item) => ({
                  size_category_id: String(item.size_category_id || ''),
                  code_pattern: item.product_code || item.tile_code || item.code_pattern || '',
                  quantity_cartons: String(item.quantity_cartons || item.quantity || ''),
                  unit_price: String(
                    item.final_price_per_unit || item.base_price_per_unit || item.unit_price || ''
                  )
                }))
              : [{ size_category_id: '', code_pattern: '', quantity_cartons: '', unit_price: '' }]
        }));

        // Also set the selected customer for zone detection
        if (editOrder.distributor_id) {
          // Customer will be set once distributors load
        }
      }
    }
  }, [isOpen, editOrder]);

  // ESC key handler to close modal
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isOpen && !loading) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, loading]);

  // Fetch company-specific data when company is selected
  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!formData.manufacturing_company_id) {
        setCodePrices([]);
        setPriceZones([]);
        setCompanyProducts([]);
        setSizeWeights([]);
        return;
      }

      setLoadingCompanyData(true);
      try {
        const [pricesRes, zonesRes, productsRes, weightsRes] = await Promise.all([
          getCodePrices(formData.manufacturing_company_id),
          getPriceZones(formData.manufacturing_company_id),
          getCompanyProducts(formData.manufacturing_company_id),
          getCompanySizeWeights(formData.manufacturing_company_id)
        ]);

        setCodePrices(Array.isArray(pricesRes) ? pricesRes : []);
        setPriceZones(Array.isArray(zonesRes) ? zonesRes : []);
        setCompanyProducts(Array.isArray(productsRes) ? productsRes : []);
        setSizeWeights(Array.isArray(weightsRes) ? weightsRes : []);

        // Re-determine customer zone with new zones
        if (selectedCustomer?.state) {
          determineCustomerZone(selectedCustomer.state, Array.isArray(zonesRes) ? zonesRes : []);
        }
      } catch (err) {
        console.error('Error fetching company pricing data:', err);
        setCodePrices([]);
        setPriceZones([]);
        setCompanyProducts([]);
        setSizeWeights([]);
      } finally {
        setLoadingCompanyData(false);
      }
    };

    fetchCompanyData();
  }, [formData.manufacturing_company_id]);

  // Determine customer zone when customer changes
  useEffect(() => {
    if (selectedCustomer?.state && priceZones.length > 0) {
      determineCustomerZone(selectedCustomer.state, priceZones);
    } else {
      setCustomerZone(null);
    }
  }, [selectedCustomer, priceZones]);

  // Auto-populate size_category_id for items if missing (fix for edit mode)
  useEffect(() => {
    if (isEditMode && companyProducts.length > 0 && formData.items.length > 0) {
      setFormData((prev) => {
        let hasChanges = false;
        const updatedItems = prev.items.map((item) => {
          // If size_category_id is already set, skip
          if (
            item.size_category_id &&
            item.size_category_id !== '' &&
            item.size_category_id !== 'null'
          )
            return item;

          // Try to find product by code pattern
          if (item.code_pattern) {
            const product = companyProducts.find(
              (p) => p.tile_code === item.code_pattern || p.product_code === item.code_pattern
            );

            if (product && product.size_category_id) {
              hasChanges = true;
              return {
                ...item,
                size_category_id: String(product.size_category_id)
              };
            }
          }
          return item;
        });

        // Only update state if changes were made to avoid infinite loops
        if (hasChanges) {
          return { ...prev, items: updatedItems };
        }
        return prev;
      });
    }
  }, [companyProducts, isEditMode]);

  // Set selectedCustomer when distributors load in edit mode
  useEffect(() => {
    if (isEditMode && editOrder?.distributor_id && distributors.length > 0 && !selectedCustomer) {
      const customer = distributors.find((d) => d.id === parseInt(editOrder.distributor_id));
      if (customer) {
        setSelectedCustomer(customer);
      }
    }
  }, [isEditMode, editOrder, distributors, selectedCustomer]);

  const determineCustomerZone = (customerState, zones) => {
    const zone = zones.find(
      (z) => z.states && Array.isArray(z.states) && z.states.includes(customerState)
    );
    setCustomerZone(zone || null);
  };

  const fetchData = async () => {
    setLoadingData(true);
    try {
      const [distRes, compRes, sizeRes] = await Promise.all([
        getDistributors({ status: 'active' }),
        getCompanies({ status: 'active' }),
        getSizeCategories()
      ]);

      // Helper to extract array from various response formats
      const extractArray = (res) => {
        if (Array.isArray(res)) return res;
        if (Array.isArray(res?.data)) return res.data;
        if (Array.isArray(res?.distributors)) return res.distributors;
        if (Array.isArray(res?.companies)) return res.companies;
        return [];
      };

      setDistributors(extractArray(distRes));
      setCompanies(extractArray(compRes));
      setSizeCategories(Array.isArray(sizeRes) ? sizeRes : []);
    } catch (err) {
      console.error('Error fetching data:', err);
      const errorMsg = err?.response?.data?.message || err?.message || 'Failed to load data';
      toast.error(errorMsg);
      setDistributors([]);
      setCompanies([]);
      setSizeCategories([]);
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // When customer changes, update selectedCustomer
    if (name === 'distributor_id' && value) {
      const customer = distributors.find((d) => d.id === parseInt(value));
      setSelectedCustomer(customer || null);
    } else if (name === 'distributor_id' && !value) {
      setSelectedCustomer(null);
    }
  };

  const handleItemChange = (index, field, value) => {
    setFormData((prev) => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      return { ...prev, items: newItems };
    });
  };

  const handleSizeCategoryChange = (index, sizeCategoryId) => {
    // Reset code_pattern and unit_price when size changes
    setFormData((prev) => {
      const newItems = [...prev.items];
      newItems[index] = {
        ...newItems[index],
        size_category_id: sizeCategoryId,
        code_pattern: '',
        unit_price: ''
      };
      return { ...prev, items: newItems };
    });
  };

  const handleProductSelect = (index, tileCode) => {
    // Find the product to get details
    const product = companyProducts.find((p) => (p.tile_code || p.product_code) === tileCode);

    let autoPrice = '';

    // Calculate price based on patterns if we have a zone
    if (product && customerZone && codePrices.length > 0) {
      const upperTileCode = tileCode.toUpperCase();

      // Sort prices by pattern length (desc) to match longest prefix first
      // Filter by size category if applicable
      const matchingPrices = codePrices
        .filter(
          (p) =>
            !p.size_category_id ||
            parseInt(p.size_category_id) === parseInt(product.size_category_id)
        )
        .sort((a, b) => b.code_pattern.length - a.code_pattern.length);

      for (const price of matchingPrices) {
        const patterns = price.code_pattern.split(',').map((p) => p.trim().toUpperCase());
        // Check for exact match or prefix match
        if (patterns.some((p) => upperTileCode === p || upperTileCode.startsWith(p))) {
          if (price.zone_prices && price.zone_prices[customerZone.id]) {
            autoPrice = price.zone_prices[customerZone.id];
            break; // Stop at first match (longest due to sort)
          }
        }
      }
    }

    // Fallback to product's base_price if no zone price was found
    if (!autoPrice && product && product.base_price) {
      autoPrice = String(product.base_price);
    }

    setFormData((prev) => {
      const newItems = [...prev.items];
      newItems[index] = {
        ...newItems[index],
        code_pattern: tileCode, // Store the actual tile code
        unit_price: autoPrice
      };
      return { ...prev, items: newItems };
    });
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        { size_category_id: '', code_pattern: '', quantity_cartons: '', unit_price: '' },
        ...prev.items
      ]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity_cartons) || 0;
      const price = parseFloat(item.unit_price) || 0;
      return sum + qty * price;
    }, 0);
  };

  // Get weight per carton for a size category
  const getWeightPerCarton = (sizeCategoryId) => {
    if (!sizeCategoryId || sizeWeights.length === 0) return 0;
    const weight = sizeWeights.find(
      (w) => parseInt(w.size_category_id) === parseInt(sizeCategoryId)
    );
    return parseFloat(weight?.weight_per_carton) || 0;
  };

  // Calculate tonnage for a single item (in tons)
  const calculateItemTonnage = (item) => {
    const qty = parseFloat(item.quantity_cartons) || 0;
    const weightPerCarton = getWeightPerCarton(item.size_category_id);
    return (qty * weightPerCarton) / 1000; // Convert kg to tons
  };

  // Calculate total tonnage for all items
  const calculateTotalTonnage = () => {
    return formData.items.reduce((sum, item) => {
      return sum + calculateItemTonnage(item);
    }, 0);
  };

  // Get products filtered by size category
  // If currentCode is provided (edit mode), ensure it's always included
  const getFilteredProducts = (sizeCategoryId, currentCode = null) => {
    if (!sizeCategoryId) return [];

    const targetSizeId = parseInt(sizeCategoryId);

    // Filter companyProducts by size category
    let filtered = companyProducts.filter((p) => {
      if (p.size_category_id === null || p.size_category_id === undefined) {
        return false;
      }
      return parseInt(p.size_category_id) === targetSizeId;
    });

    // In edit mode, ensure the current product is included even if it doesn't match filter
    if (currentCode && !filtered.some((p) => (p.tile_code || p.product_code) === currentCode)) {
      const existingProduct = companyProducts.find(
        (p) => (p.tile_code || p.product_code) === currentCode
      );
      if (existingProduct) {
        filtered = [existingProduct, ...filtered];
      }
    }

    return filtered;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.distributor_id) {
      toast.error('Please select a customer');
      return;
    }
    if (!formData.manufacturing_company_id) {
      toast.error('Please select a manufacturing company');
      return;
    }
    const validItems = formData.items.filter((item) => item.code_pattern && item.quantity_cartons);
    if (validItems.length === 0) {
      toast.error('Please add at least one order item');
      return;
    }

    // Validate item quantities and prices
    for (let i = 0; i < validItems.length; i++) {
      const item = validItems[i];
      const qty = parseFloat(item.quantity_cartons);
      const price = parseFloat(item.unit_price);

      if (isNaN(qty) || qty <= 0) {
        toast.error(`Item ${i + 1}: Quantity must be greater than 0`);
        return;
      }
      if (qty > 100000) {
        toast.error(`Item ${i + 1}: Quantity is too large (max: 100,000 sqm)`);
        return;
      }
      if (item.unit_price && (isNaN(price) || price < 0)) {
        toast.error(`Item ${i + 1}: Unit price cannot be negative`);
        return;
      }
      if (price > 1000000) {
        toast.error(`Item ${i + 1}: Unit price is too large (max: ₦1,000,000)`);
        return;
      }
    }

    setLoading(true);
    try {
      // Get size category names for the items
      const getSizeName = (sizeCategoryId) => {
        const size = sizeCategories.find(
          (s) => s.id === parseInt(sizeCategoryId) || String(s.id) === sizeCategoryId
        );
        return size?.name || '';
      };

      const payload = {
        distributor_id: parseInt(formData.distributor_id),
        manufacturing_company_id: parseInt(formData.manufacturing_company_id),
        requested_delivery_date: formData.requested_delivery_date || null,
        delivery_address: formData.delivery_address,
        notes: formData.notes,
        items: validItems.map((item) => ({
          product_code: item.code_pattern,
          size_category_id: parseInt(item.size_category_id),
          product_name: `${getSizeName(item.size_category_id)} - ${item.code_pattern}`,
          quantity_cartons: parseFloat(item.quantity_cartons) || 0,
          unit_price: parseFloat(item.unit_price) || 0
        }))
      };

      if (isEditMode) {
        await updateOrder(editOrder.id, payload);
        toast.success('Order updated successfully');
        onSuccess?.();
        handleClose();
      } else {
        const response = await createOrder(payload);
        toast.success('Order created successfully');
        onSuccess?.();
        handleClose();
        // Navigate to the new order detail page
        const newOrderId = response?.data?.id || response?.id;
        if (newOrderId) {
          navigate(`/wholesale/orders/${newOrderId}`);
        }
      }
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} order:`, err);
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        `Failed to ${isEditMode ? 'update' : 'create'} order`;
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      distributor_id: '',
      manufacturing_company_id: '',
      requested_delivery_date: '',
      delivery_address: '',
      notes: '',
      items: [{ size_category_id: '', code_pattern: '', quantity_cartons: '', unit_price: '' }]
    });
    setSelectedCustomer(null);
    setCustomerZone(null);
    setCodePrices([]);
    setPriceZones([]);
    setCompanyProducts([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={handleClose} />

        <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {isEditMode ? 'Edit Order' : 'Create Order'}
                </h2>
                <p className="text-sm text-gray-500">
                  {isEditMode
                    ? `Editing order ${editOrder.order_number}`
                    : 'Create a new wholesale order'}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {loadingData ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-emerald-500 border-t-transparent"></div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="p-6 space-y-6">
                {/* Customer & Company Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <User className="w-4 h-4 inline mr-1" />
                      Customer <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="distributor_id"
                      value={formData.distributor_id}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      required
                    >
                      <option value="">Select customer</option>
                      {distributors.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.business_name} {d.state ? `(${d.state})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Building2 className="w-4 h-4 inline mr-1" />
                      Manufacturing Company <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="manufacturing_company_id"
                      value={formData.manufacturing_company_id}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      required
                    >
                      <option value="">Select company</option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.company_name || c.name} {c.is_ftz ? '(FTZ)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Delivery Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      Requested Delivery Date
                    </label>
                    <input
                      type="date"
                      name="requested_delivery_date"
                      value={formData.requested_delivery_date}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Delivery Address
                    </label>
                    <input
                      type="text"
                      name="delivery_address"
                      value={formData.delivery_address}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="Leave blank for default address"
                    />
                  </div>
                </div>

                {/* Order Items */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Order Items
                      </h3>
                      {/* Customer Zone Badge */}
                      {selectedCustomer && (
                        <div className="flex items-center gap-2">
                          {customerZone ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                              <MapPin className="w-3 h-3" />
                              {customerZone.zone_name} Zone
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                              <MapPin className="w-3 h-3" />
                              No Zone - {selectedCustomer.state || 'No State'}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={addItem}
                      className="inline-flex items-center gap-1 px-3 py-1 text-sm text-emerald-600 hover:bg-emerald-50 rounded-lg"
                    >
                      <Plus className="w-4 h-4" />
                      Add Item
                    </button>
                  </div>

                  {!formData.manufacturing_company_id && (
                    <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
                      Please select a Manufacturing Company to add items
                    </div>
                  )}

                  {formData.manufacturing_company_id && loadingCompanyData && (
                    <div className="flex items-center justify-center py-6">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-emerald-500 border-t-transparent"></div>
                      <span className="ml-2 text-sm text-gray-500">Loading products...</span>
                    </div>
                  )}

                  {formData.manufacturing_company_id && !loadingCompanyData && (
                    <div className="space-y-3">
                      {companyProducts.length === 0 && (
                        <div className="text-center py-4 text-orange-600 bg-orange-50 rounded-lg">
                          No products configured for this company. Please add products in Company
                          Settings.
                        </div>
                      )}

                      {companyProducts.length > 0 &&
                        formData.items.map((item, index) => (
                          <div
                            key={index}
                            className="flex gap-3 items-start p-3 bg-gray-50 rounded-lg"
                          >
                            <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-3">
                              {/* Size Category - First */}
                              <select
                                value={String(item.size_category_id || '')}
                                onChange={(e) => handleSizeCategoryChange(index, e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                required
                              >
                                <option value="">Size *</option>
                                {sizeCategories.map((s) => (
                                  <option key={s.id} value={String(s.id)}>
                                    {s.name}
                                  </option>
                                ))}
                              </select>

                              {/* Product/Tile Code - Second (filtered by size) */}
                              <select
                                value={item.code_pattern || ''}
                                onChange={(e) => handleProductSelect(index, e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                style={{ maxHeight: '160px', overflowY: 'auto' }}
                                disabled={!item.size_category_id}
                                required
                              >
                                <option value="">Product *</option>
                                {/* Fallback: show current code if not in products list */}
                                {item.code_pattern &&
                                  !companyProducts.some(
                                    (p) => (p.tile_code || p.product_code) === item.code_pattern
                                  ) && (
                                    <option value={item.code_pattern}>
                                      {item.code_pattern} (Current)
                                    </option>
                                  )}
                                {getFilteredProducts(item.size_category_id, item.code_pattern).map(
                                  (p) => (
                                    <option key={p.id} value={p.tile_code || p.product_code}>
                                      {p.tile_code || p.product_code} {p.name ? `(${p.name})` : ''}
                                    </option>
                                  )
                                )}
                              </select>

                              {/* Quantity */}
                              <input
                                type="number"
                                placeholder="Cartons *"
                                value={item.quantity_cartons}
                                onChange={(e) =>
                                  handleItemChange(index, 'quantity_cartons', e.target.value)
                                }
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                min="0"
                                step="1"
                              />

                              {/* Unit Price - Auto-populated, editable */}
                              <input
                                type="number"
                                placeholder="Price (₦)"
                                value={item.unit_price}
                                onChange={(e) =>
                                  handleItemChange(index, 'unit_price', e.target.value)
                                }
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                min="0"
                                step="0.01"
                              />

                              {/* Tonnage - Calculated, read-only */}
                              <div className="px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-700 flex items-center">
                                <span className="text-xs text-gray-500 mr-1">Tons:</span>
                                <span className="font-medium">
                                  {calculateItemTonnage(item).toFixed(3)}
                                </span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                              disabled={formData.items.length === 1}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                    </div>
                  )}

                  {/* Totals */}
                  <div className="flex justify-end mt-4 pt-4 border-t border-gray-200">
                    <div className="text-right space-y-2">
                      <div>
                        <p className="text-sm text-gray-500">Estimated Tonnage</p>
                        <p className="text-lg font-semibold text-gray-700">
                          {calculateTotalTonnage().toFixed(3)} tons
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Estimated Total</p>
                        <p className="text-xl font-bold text-gray-900">
                          ₦{calculateTotal().toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Order Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none text-sm"
                    placeholder="Special instructions, urgent notes, etc."
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                  disabled={
                    loading || !formData.manufacturing_company_id || companyProducts.length === 0
                  }
                >
                  {loading
                    ? isEditMode
                      ? 'Updating...'
                      : 'Creating...'
                    : isEditMode
                      ? 'Update Order'
                      : 'Create Order'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateOrderModal;
